<template>
    <Head :title="project.name + ' - ' + $t('projects.templatePresets')" />
    <piped-title
        :first="project.name"
        :second="'Projekt Themen Voreinstellungen'"
        :pages="breadcrumbPages"
    ></piped-title>

    <!-- Plan vorlage-auf-wohnungen (Task 10): offene Änderungen gegenüber bestehenden Wohnungen -->
    <div
        v-if="transfer && transfer.pendingCount > 0"
        class="rounded-md bg-blue-50 border border-blue-100 p-4 my-4 flex items-center justify-between gap-4"
    >
        <div class="flex items-start">
            <InformationCircleIcon class="h-5 w-5 text-blue-400 mr-2 flex-shrink-0" />
            <p class="text-sm text-blue-800">
                {{
                    $t(
                        "templateTransfer.openChanges",
                        { count: transfer.pendingCount },
                        transfer.pendingCount
                    )
                }}
            </p>
        </div>
        <div class="flex gap-2 flex-shrink-0">
            <!-- E2: solange Themen ungespeichert sind, sind beide Knöpfe gesperrt -->
            <secondary-button
                type="button"
                :disabled="form.isDirty"
                :title="form.isDirty ? $t('actions.saveFirst') : null"
                @click="openTransfer"
            >
                {{ $t("templateTransfer.openTransfer") }}
            </secondary-button>
            <secondary-button
                type="button"
                :disabled="form.isDirty"
                :title="
                    form.isDirty
                        ? $t('actions.saveFirst')
                        : $t('templateTransfer.dismiss')
                "
                :aria-label="$t('templateTransfer.dismiss')"
                @click="askOnlyNew('banner')"
            >
                <TrashIcon
                    class="h-5 w-5 text-minplan-darkgreybeige"
                    aria-hidden="true"
                />
            </secondary-button>
        </div>
    </div>

    <manage-topics
        :form="form"
        :suppliers="suppliers"
        :options="options"
        :type="'project'"
        :applyLink="route('advisors.projects.edit.template', this.project.uuid)"
        :saveLink="route('advisors.projects.updateTopics', this.project.uuid)"
        :goBackLink="route('advisors.projects.show', this.project.uuid)"
    ></manage-topics>

    <!-- Frage nach dem Speichern -->
    <dialog-modal :show="showAsk" @close="showAsk = false">
        <template #title>{{ $t("templateTransfer.askTitle") }}</template>
        <template #content>
            <p class="text-sm text-gray-700">
                {{
                    $t(
                        "templateTransfer.askText",
                        { count: transfer?.pendingCount ?? 0 },
                        transfer?.pendingCount ?? 0
                    )
                }}
            </p>
        </template>
        <template #footer>
            <div class="flex items-center">
                <button
                    type="button"
                    class="text-sm font-semibold mr-auto"
                    @click="showAsk = false"
                >
                    {{ $t("templateTransfer.askLater") }}
                </button>
                <secondary-button
                    class="mr-3"
                    type="button"
                    :disabled="dismissing"
                    @click="askOnlyNew('ask')"
                >
                    {{ $t("templateTransfer.askOnlyNew") }}
                </secondary-button>
                <primary-button type="button" @click="openTransfer">
                    {{ $t("templateTransfer.askTransfer") }}
                </primary-button>
            </div>
        </template>
    </dialog-modal>

    <!-- Rückfrage vor «Nur für neue Wohnungen»: dasselbe Fenster für den Knopf in der Frage
         und für den Papierkorb im Hinweis -->
    <dialog-modal :show="showOnlyNew" max-width="md" @close="cancelOnlyNew">
        <template #title>{{ $t("templateTransfer.onlyNewTitle") }}</template>
        <template #content>
            <p class="text-sm text-gray-700">
                {{
                    $t(
                        "templateTransfer.onlyNewText",
                        { count: transfer?.pendingCount ?? 0 },
                        transfer?.pendingCount ?? 0
                    )
                }}
            </p>
        </template>
        <template #footer>
            <secondary-button
                ref="cancelOnlyNew"
                class="mr-3"
                type="button"
                @click="cancelOnlyNew"
            >
                {{ $t("actions.cancel") }}
            </secondary-button>
            <!-- Klicksperre: der Knopf liegt dort, wo eben «Nur für neue Wohnungen» war -->
            <primary-button
                type="button"
                :disabled="dismissing || !onlyNewArmed"
                :class="{ 'opacity-25': dismissing }"
                @click="dismiss"
            >
                {{ $t("templateTransfer.askOnlyNew") }}
            </primary-button>
        </template>
    </dialog-modal>

    <transfer-template-dialog
        v-if="transfer"
        :show="showTransfer"
        :project-uuid="project.uuid"
        :apartments="transfer.apartments"
        @close="showTransfer = false"
        @done="finishTransfer"
    />
</template>

<script>
import { Head } from "@inertiajs/inertia-vue3";
import { Inertia } from "@inertiajs/inertia";
import { defineComponent } from "vue";
import MainLayout from "@/Layouts/MainLayout";
import PipedTitle from "@/Shared/Components/PipedTitle";
import { useForm } from "@inertiajs/inertia-vue3";
import PrimaryButton from "@/Jetstream/PrimaryButton";
import SecondaryButton from "@/Jetstream/SecondaryButton";
import DialogModal from "@/Jetstream/DialogModal";
import {
    ArrowLeftIcon,
    ExclamationIcon,
    InformationCircleIcon,
    TemplateIcon,
    TrashIcon,
} from "@heroicons/vue/outline";
import ManageTopics from "@/Pages/Templates/Components/ManageTopics";
import TransferTemplateDialog from "@/Pages/Projects/Components/TransferTemplateDialog";

export default defineComponent({
    props: {
        project: Object,
        topics: Object,
        suppliers: Array,
        options: Array,
        transfer: Object,
    },
    layout: MainLayout,
    components: {
        Head,
        PipedTitle,
        PrimaryButton,
        SecondaryButton,
        DialogModal,
        ArrowLeftIcon,
        ExclamationIcon,
        InformationCircleIcon,
        TemplateIcon,
        TrashIcon,
        ManageTopics,
        TransferTemplateDialog,
    },
    setup(props) {
        const form = useForm({
            topics: props.topics.map((d) => d),
        });

        return {
            form,
        };
    },
    data() {
        return {
            showAsk: false,
            showOnlyNew: false,
            // woher die Rückfrage kam: "ask" (Frage nach dem Speichern) oder "banner" (Papierkorb)
            onlyNewFrom: null,
            // Klicksperre: 400 ms nach dem Öffnen kann nichts bestätigt werden
            onlyNewArmed: false,
            showTransfer: false,
            dismissing: false,
        };
    },
    mounted() {
        // Nach dem Speichern (Redirect mit `saved=1`) fragen, ob übertragen werden soll.
        // `$page.url` statt `window.location`: beim Mount nach dem Redirect hat Inertia
        // die Adresszeile noch nicht nachgeführt.
        const justSaved = /[?&]saved=1/.test(this.$page.url);
        this.loadTransfer(justSaved);
    },
    beforeUnmount() {
        clearTimeout(this.armTimer);
        clearTimeout(this.focusTimer);
    },
    methods: {
        // Offene Änderungen nachladen (lazy Prop), damit die Seite sofort erscheint.
        loadTransfer(askAfterwards = false) {
            // gleichzeitig `saved=1` aus der Adresse entfernen, sonst fragt ein Neuladen erneut
            const url = this.$page.url
                .replace(/([?&])saved=1(&|$)/, (m, a, b) => (b ? a : ""))
                .replace(/\?$/, "");
            Inertia.visit(url, {
                only: ["transfer"],
                replace: true,
                preserveScroll: true,
                preserveState: true,
                onSuccess: () => {
                    if (askAfterwards) this.showAsk = this.transfer?.pendingCount > 0;
                },
            });
        },
        openTransfer() {
            if (this.form.isDirty) return;
            this.showAsk = false;
            this.showTransfer = true;
        },
        // Rückfrage öffnen; «Abbrechen» hat den Fokus, damit ein schnelles Enter nichts verwirft
        askOnlyNew(from) {
            if (this.form.isDirty) return;
            this.onlyNewFrom = from;
            this.showAsk = false;
            this.showOnlyNew = true;
            this.onlyNewArmed = false;
            // die Sperre eines früheren Öffnens darf diese nicht vorzeitig aufheben
            clearTimeout(this.armTimer);
            clearTimeout(this.focusTimer);
            this.armTimer = setTimeout(() => (this.onlyNewArmed = true), 400);
            this.$nextTick(() => {
                clearTimeout(this.focusTimer);
                this.focusTimer = setTimeout(
                    () => this.$refs.cancelOnlyNew?.$el?.focus(),
                    50
                );
            });
        },
        // aus der Frage kommend zurück zur Frage, vom Papierkorb kommend nur schliessen
        cancelOnlyNew() {
            this.showOnlyNew = false;
            if (this.onlyNewFrom === "ask") this.showAsk = true;
        },
        dismiss() {
            // Doppelklick: ein zweiter Klick löst keinen zweiten Request aus
            if (this.dismissing || this.form.isDirty) return;
            this.dismissing = true;
            this.showAsk = false;
            this.showOnlyNew = false;
            this.$inertia.post(
                this.route(
                    "advisors.projects.templateTransfer.dismiss",
                    this.project.uuid
                ),
                {},
                { preserveScroll: true, onFinish: () => (this.dismissing = false) }
            );
        },
        finishTransfer() {
            this.showTransfer = false;
            Inertia.visit(
                this.route("advisors.projects.manageTopics", this.project.uuid),
                {
                    preserveScroll: true,
                }
            );
        },
    },
    computed: {
        breadcrumbPages() {
            return [
                {
                    name: this.$t("projects.projects"),
                    href: this.route("advisors.projects.index"),
                    current: true,
                },
                {
                    name: this.project.name,
                    href: this.route("advisors.projects.show", this.project.uuid),
                    current: false,
                },
                {
                    name: this.$t("projects.templatePresets"),
                    href: "#",
                    current: true,
                },
            ];
        },
    },
});
</script>

<style scoped></style>
